import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
  useAIPanel,
} from '../components/ConstareAIPanel';
import { FourChecksDiagram } from '../components/lesson/FourChecksDiagram';

/**
 * Lesson 1.4 visual preview at /preview/lesson/1.4
 *
 * "Your First Audit" — students learn a repeatable 4-check audit process
 * (Date, Source, Reasoning, Confidence) and apply it to 3 real AI outputs.
 * Promotes "Training cutoff" in as the lead vocabulary term with the same
 * 4-block treatment used in 1.3. Includes one guided exercise (the audit
 * walkthrough) plus three hands-on audit drills with click-to-flag claims
 * and a model audit reveal.
 */

export default function LessonPreviewPage1_4() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

function LessonPage() {
  const totalSections = 6;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const ids = [
      'sec-cold-open',
      'sec-from-last-time',
      'sec-teaching',
      'sec-your-turn',
      'sec-quick-check',
      'sec-lock-card',
    ];
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = ids.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[200px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <ColdOpen />
          <FromLastTime />
          <Teaching />
          <YourTurn />
          <QuickCheck />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

// ---------- Header ----------

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 1 · Foundations · Lesson 1.4
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        Your First Audit
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 45 min
        </span>
        <span className="h-3 w-px bg-line" />
        <span>Threads: Judgment · Character</span>
        <span className="h-3 w-px bg-line" />
        <span className="text-amber">3 hands-on audit drills</span>
      </div>
    </header>
  );
}

// ---------- Cold open ----------

function ColdOpen() {
  return (
    <Section
      id="sec-cold-open"
      label="Cold open"
      eyebrow="Read this once. Believe it or not?"
      accent="amber"
    >
      <div className="my-2 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          AI response · January 2026
        </p>
        <p className="mt-3 text-text">
          The Dallas Mavericks won the 2025 NBA Finals in six games against the
          Boston Celtics, with Luka Dončić averaging 32 points per game in the
          series and earning Finals MVP honors. It was the franchise's second
          championship after their 2011 title.
        </p>
      </div>
      <p>
        Sounds confident. Sounds plausible. Sounds like the kind of answer you
        would copy into a paper without thinking twice.
      </p>
      <p>
        Every single sentence in that paragraph is wrong. The Mavericks did not
        play in the 2025 Finals. The series was not against Boston. Luka was
        not on the roster by the time the playoffs started. The MVP went to
        someone on a different team entirely.
      </p>
      <p>
        The AI did not lie to you on purpose. It did exactly what Lesson 1.2
        showed you: it predicted the next token, one at a time, until the
        sentences sounded right. It had no idea what actually happened.
      </p>
      <p className="text-text">
        Your job for the next 45 minutes is to learn how to catch this. Not
        sometimes. Every time.
      </p>
    </Section>
  );
}

// ---------- From last time ----------

function FromLastTime() {
  return (
    <Section
      id="sec-from-last-time"
      label="From last time"
      eyebrow="Two minutes. No looking back at Lesson 1.3."
      accent="blue"
    >
      <ol className="space-y-3 text-text">
        <li>
          <span className="font-mono text-amber">01.</span> What is a
          hallucination, in one sentence?
        </li>
        <li>
          <span className="font-mono text-amber">02.</span> Why does an AI
          sound just as confident when it's wrong as when it's right?
        </li>
        <li>
          <span className="font-mono text-amber">03.</span> Name one thing
          temperature controls and one thing it does not.
        </li>
      </ol>
      <p className="mt-5 text-sm text-muted">
        Hold your answers. The audit you are about to learn is the practical
        application of every term in 1.3.
      </p>
    </Section>
  );
}

// ---------- Teaching ----------

function Teaching() {
  return (
    <Section
      id="sec-teaching"
      label="The teaching"
      eyebrow="One framework. Four checks. Used in this order, every time."
    >
      <Subhead n="1" title="What an audit actually is" />
      <p>
        An audit is a structured way to ask the question: <Vocab>is this
        actually true, or does it just sound true?</Vocab> You apply it to AI
        output the same way a journalist applies it to a source, or an auditor
        applies it to a financial statement. The point is not to be paranoid.
        The point is to be useful.
      </p>
      <p>
        Most people audit AI output by squinting at it and feeling vaguely
        uneasy. That is not auditing. That is hoping. An audit is a series of
        specific checks you can run on any AI response in under five minutes,
        and it almost always finds something.
      </p>

      <Subhead n="2" title="Training cutoff" />
      <p>
        Before we get to the four checks, one piece of vocabulary needs a
        proper introduction. Training cutoff is the single most common cause
        of confidently wrong AI answers, and it is the first thing you check.
      </p>

      <VocabCard
        term="Training cutoff"
        definition="The date after which the model has not seen any new data. Anything that happened after the training cutoff, the model does not know about, unless it has tools to look it up at the time of inference."
        analogy="A friend who went on a six-month silent retreat with no internet. Brilliant, well-read, but completely missing the last six months of news, slang, sports results, and current events."
        hook='Ask any AI: "What is your training cutoff?" The answer is the line between what it actually knows and what it has to guess at. The right answer to "who won last night" depends entirely on this date.'
        matters="This is the single most common cause of confidently wrong AI answers about current events. You ask 'what is the latest version of this software' and get an answer 18 months out of date. You ask 'who won the election' and get the previous one. The model has no way to know it is talking about something that happened after its cutoff."
      />

      <Subhead n="3" title="The four checks, in order" />
      <p>
        Run these in this order, every time. The order matters because the
        earliest check usually catches the biggest problem, and you do not
        want to waste effort on the later ones if the first one already failed.
      </p>

      <div className="space-y-4">
        <CheckCard
          n={1}
          name="Date check"
          summary="Could this answer depend on something that happened after the model's training cutoff?"
          details={
            <>
              <p>
                Ask: is the topic time-sensitive? News, sports, prices,
                product versions, current events, leadership changes, who-just-
                acquired-whom, the latest anything. All of it depends on a date.
              </p>
              <p>
                If yes: the AI either looked it up at inference time (RAG, web
                search, tools) or it is guessing based on patterns from before
                its cutoff. You need to know which one. If the AI did not cite
                a source it looked up, assume it guessed.
              </p>
            </>
          }
        />
        <CheckCard
          n={2}
          name="Source check"
          summary="For every factual claim, where did it come from, and can you actually find that source?"
          details={
            <>
              <p>
                AI loves to attribute things to sources that do not exist or do
                not say what the AI claims they say. Made-up court cases,
                fabricated study titles, real authors paired with books they
                did not write. This is one of the most documented failure modes
                in the field.
              </p>
              <p>
                For each citation in an AI response: search for it. If it
                exists, read it. Does it actually say what the AI claimed? You
                will be surprised how often the answer is no.
              </p>
            </>
          }
        />
        <CheckCard
          n={3}
          name="Reasoning check"
          summary="Does each step in the argument actually follow from the step before it?"
          details={
            <>
              <p>
                AI output often reads like reasoning but is actually just
                fluent narration of a vibe. Each sentence sounds smart on its
                own. Read them in sequence and ask: does sentence three
                actually follow from sentence two? Did the math check out? Did
                the conclusion use evidence the AI introduced earlier?
              </p>
              <p>
                A useful trick: cover up the conclusion and ask yourself what
                the evidence should lead to. If the AI's conclusion does not
                match yours, something broke between the steps.
              </p>
            </>
          }
        />
        <CheckCard
          n={4}
          name="Confidence check"
          summary="Is the AI hedging where it should, and sounding certain only where it has reason to be?"
          details={
            <>
              <p>
                Good AI output flags what it is unsure about. "I am not certain
                about the exact date, but..." or "I have not seen this verified
                anywhere..." or "this is a rough estimate." Bad AI output uses
                the same confident tone for everything, whether it is
                describing a settled fact or guessing at a number.
              </p>
              <p>
                When you see uniform confidence across an answer that mixes
                solid facts with speculation, that is a red flag. The model
                does not know what it does not know. You have to.
              </p>
            </>
          }
        />
      </div>

      <FourChecksDiagram />

      <Subhead n="4" title="What to do when an audit finds something" />
      <p>
        Finding a problem is not the end. It is the start. Three options:
      </p>
      <ul className="list-disc space-y-2 pl-5 text-text/90">
        <li>
          <strong className="text-text">Verify it yourself.</strong> Go look up
          the claim, check the math, find the real source. This is the
          highest-value move because you walk away knowing the answer.
        </li>
        <li>
          <strong className="text-text">Ask the AI to fix it.</strong> Point at
          the specific problem and ask for a corrected version with sources.
          Sometimes this works. Often it produces a new confidently wrong
          answer. Audit the corrected version too.
        </li>
        <li>
          <strong className="text-text">Throw it out.</strong> If the audit
          finds enough problems, the answer is unsalvageable. Use a different
          tool, ask a person, or accept that the AI does not actually know.
        </li>
      </ul>
      <p>
        Most students want to skip to option two. Resist that. The point of
        this lesson is not to get a clean answer from the AI. It is to build
        the muscle that decides whether any AI answer is worth trusting.
      </p>

      <Exercise1 />
    </Section>
  );
}

function Subhead({ n, title }: { n: string; title: string }) {
  return (
    <h3 className="mt-8 flex items-baseline gap-3 text-lg font-semibold text-text">
      <span className="font-mono text-[11px] uppercase tracking-widest text-amber">
        {n}
      </span>
      {title}
    </h3>
  );
}

// ---------- Vocab card (single-term variant for 1.4) ----------

function VocabCard({
  term,
  definition,
  analogy,
  hook,
  matters,
}: {
  term: string;
  definition: string;
  analogy: string;
  hook: string;
  matters: string;
}) {
  const [draft, setDraft] = useState('');
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="my-6 overflow-hidden rounded-xl border border-line bg-surface">
      <div className="border-b border-line bg-elevated/40 px-5 py-3">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
          Vocab · 1.4 · Promoted from 1.3
        </p>
        <h4 className="mt-1 text-xl font-semibold text-text">{term}</h4>
      </div>
      <div className="space-y-4 px-5 py-5">
        {!revealed && (
          <>
            <p className="text-sm text-muted">
              Write your own one-sentence definition first. Then reveal.
            </p>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={`Your definition of "${term}"...`}
              className="h-20 w-full resize-none rounded-md border border-line bg-ink/60 px-3 py-2 text-sm text-text placeholder:text-muted/60 focus:border-amber focus:outline-none"
            />
            <button
              onClick={() => setRevealed(true)}
              className="rounded-md bg-amber px-4 py-2 text-xs font-semibold text-ink hover:bg-amber-hover"
            >
              Reveal definition →
            </button>
          </>
        )}
        {revealed && (
          <div className="space-y-3">
            <Block label="Definition" body={definition} />
            <Block label="Analogy" body={analogy} accent="amber" />
            <Block label="Memory hook" body={hook} />
            <Block label="Why it matters" body={matters} />
          </div>
        )}
      </div>
    </div>
  );
}

function Block({
  label,
  body,
  accent = 'plain',
}: {
  label: string;
  body: string;
  accent?: 'plain' | 'amber';
}) {
  return (
    <div
      className={`rounded-md border px-4 py-3 ${
        accent === 'amber'
          ? 'border-amber/40 bg-amber/[0.04]'
          : 'border-line bg-ink/40'
      }`}
    >
      <p
        className={`font-mono text-[10px] uppercase tracking-widest ${
          accent === 'amber' ? 'text-amber' : 'text-muted'
        }`}
      >
        {label}
      </p>
      <p className="mt-1.5 text-sm leading-relaxed text-text/90">{body}</p>
    </div>
  );
}

// ---------- Check card ----------

function CheckCard({
  n,
  name,
  summary,
  details,
}: {
  n: number;
  name: string;
  summary: string;
  details: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="overflow-hidden rounded-lg border border-line bg-surface">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-4 px-5 py-4 text-left hover:bg-elevated/40"
      >
        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-amber font-mono text-sm font-semibold text-ink">
          {n}
        </span>
        <div className="flex-1">
          <p className="text-base font-semibold text-text">{name}</p>
          <p className="mt-1 text-sm text-muted">{summary}</p>
        </div>
        <span className="mt-0.5 shrink-0 font-mono text-xs text-muted">
          {open ? '−' : '+'}
        </span>
      </button>
      {open && (
        <div className="space-y-3 border-t border-line bg-ink/40 px-5 py-4 text-sm text-text/90">
          {details}
        </div>
      )}
    </div>
  );
}

// ---------- Exercise 1: Make the AI audit itself ----------

const EX1_PROMPT = `I am going to give you a paragraph that you wrote. I want you to audit your own paragraph using four checks, in this order:

1. Date check: does anything in this paragraph depend on information after your training cutoff?
2. Source check: list every factual claim and tell me whether you could cite a real source for it.
3. Reasoning check: does each sentence actually follow from the one before it?
4. Confidence check: for each claim, how confident are you, honestly?

Here is the paragraph to audit:

"The Dallas Mavericks won the 2025 NBA Finals in six games against the Boston Celtics, with Luka Dončić averaging 32 points per game in the series and earning Finals MVP honors. It was the franchise's second championship after their 2011 title."`;

const EX1_MOCK = `Auditing my own paragraph honestly.

1. Date check
The paragraph claims a result from the 2025 NBA Finals. My training cutoff is in 2025, and depending on when in 2025 it landed, I may or may not have data on the Finals result. I should not have written this as a settled fact without flagging that. Fail.

2. Source check
- "Mavericks won the 2025 Finals" — I cannot cite a source for this. I generated it as a plausible-sounding sports outcome. Fail.
- "Six games against Boston" — Same. No source. Fail.
- "Luka averaged 32 points and was Finals MVP" — Same. I do not have a verified box score. Fail.
- "Franchise's second championship after 2011" — The 2011 title is real and I am confident in it. Pass.

3. Reasoning check
The paragraph is not making an argument. It is stating four claims as if they were independent facts. Three of them are unsupported. The paragraph does not actually reason; it just narrates. Fail.

4. Confidence check
The tone of the paragraph is uniformly confident. There are no hedges, no "I think," no "I have not verified." That is the bigger problem here. A correct version would have said: "I do not know who won the 2025 Finals" or "I cannot confirm this without looking it up." Fail.

Honest verdict on the paragraph: one true claim out of five, written with confidence appropriate to zero of them. This is exactly the kind of output you should throw out, not patch.`;

function Exercise1() {
  const ai = useAIPanel();
  return (
    <ExerciseShell
      number={1}
      title="Make the AI audit its own bad answer"
      estMinutes={5}
      type="guided"
    >
      <p>
        Before we put the audit on you, let's watch the AI run it on itself.
        We are going to feed it the cold-open paragraph (the confidently wrong
        Mavericks paragraph) and ask it to apply the four checks. Read the
        response carefully. This is the shape your own audits should take.
      </p>
      <div className="rounded-md border border-line bg-ink/60 p-3 font-mono text-xs text-text">
        {EX1_PROMPT}
      </div>
      <button
        onClick={() => ai.loadChatPrompt(EX1_PROMPT, EX1_MOCK)}
        className="rounded-md bg-amber px-4 py-2 text-xs font-semibold text-ink hover:bg-amber-hover"
      >
        Load prompt into AI panel →
      </button>
      <p>
        Two things to notice. First, the AI can audit its own work, but only
        when you give it the framework. Without the four checks, it would
        defend its first answer. Second, the honest audit is far more useful
        than the original paragraph. Most AI conversations should end with
        some version of this audit, even if you have to ask for it explicitly.
      </p>
    </ExerciseShell>
  );
}

// ---------- Your turn: 3 audit drills ----------

type Drill = {
  n: number;
  title: string;
  estMinutes: number;
  category: string;
  promptShown: string;
  aiResponse: string;
  flags: Flag[];
  verdict: string;
};

type Flag = {
  id: string;
  span: string;
  issue: string;
  check: 'Date' | 'Source' | 'Reasoning' | 'Confidence';
};

const DRILLS: Drill[] = [
  {
    n: 1,
    title: 'Research summary',
    estMinutes: 6,
    category: 'Facts and sources',
    promptShown: 'Summarize what we know about teenage sleep and academic performance.',
    aiResponse: `Research consistently shows that teenagers need 8 to 10 hours of sleep per night for optimal academic performance. A landmark 2019 Stanford study by Dr. Karen Mitchell found that students who got fewer than 7 hours of sleep scored 15% lower on standardized tests than well-rested peers. The American Academy of Pediatrics has recommended that high schools start no earlier than 8:30 AM since 2014, and districts that have adopted this policy report a 23% increase in graduation rates within two years.`,
    flags: [
      {
        id: 'd1-a',
        span: 'A landmark 2019 Stanford study by Dr. Karen Mitchell',
        issue: 'No such study or researcher comes up in any search. This is a fabricated citation. The 8-to-10-hours figure is real (it comes from the AAP and NSF), but this specific study attribution is invented.',
        check: 'Source',
      },
      {
        id: 'd1-b',
        span: 'scored 15% lower on standardized tests',
        issue: 'Precise-sounding numbers attached to fake studies. Even the real research on sleep and grades does not produce a clean number like "15%" — effects vary by subject, age, and methodology.',
        check: 'Source',
      },
      {
        id: 'd1-c',
        span: 'a 23% increase in graduation rates within two years',
        issue: 'This is the worst kind of statistic: specific, dramatic, and unsourced. The real research on later school start times shows modest improvements in attendance and mental health, not enormous swings in graduation rates.',
        check: 'Source',
      },
    ],
    verdict:
      'Two real facts (sleep need, AAP recommendation date) wrapped around three fabricated specifics. Throw out and start over with a real source like the AAP policy statement or the CDC Healthy Schools data.',
  },
  {
    n: 2,
    title: 'Math and logic',
    estMinutes: 5,
    category: 'Reasoning',
    promptShown:
      'I have a 12-foot ladder. It needs to lean against a wall so the top is exactly 10 feet off the ground. How far from the wall should the base of the ladder be?',
    aiResponse: `Great question. We can solve this using the Pythagorean theorem. The ladder is the hypotenuse (12 feet), the height up the wall is one leg (10 feet), and the distance from the wall is the other leg, which we'll call x.

So we have: 12² = 10² + x²

Working through it: 144 = 100 + x², which means x² = 44, so x = √44, which is approximately 6.6 feet.

You should place the ladder approximately 6.6 feet from the wall to reach exactly 10 feet up.`,
    flags: [
      {
        id: 'd2-a',
        span: 'x = √44, which is approximately 6.6 feet',
        issue: '√44 is actually about 6.63 — so the 6.6 estimate is fine. But check the full setup: every step of the math is correct here. This drill is a trap. Sometimes the AI is right and your audit should land on "passes all four checks."',
        check: 'Reasoning',
      },
      {
        id: 'd2-b',
        span: 'You should place the ladder approximately 6.6 feet from the wall',
        issue: 'Mathematically correct. Practically dangerous. OSHA and most ladder safety guides recommend a 4:1 ratio (1 foot base for every 4 feet of height), which would put the base around 2.5 feet from the wall. The AI answered the math question but missed that the question was probably about ladder safety.',
        check: 'Reasoning',
      },
    ],
    verdict:
      'Math is correct. The deeper problem is that the AI answered the literal question instead of the actual question. This is a reasoning failure at the framing level: the answer assumes you wanted geometry, when most people asking this want safety. A good response would solve the math AND flag the safety issue.',
  },
  {
    n: 3,
    title: 'Recommendation',
    estMinutes: 7,
    category: 'All four checks',
    promptShown:
      'My laptop battery only lasts 2 hours now. What is the best replacement battery I can buy?',
    aiResponse: `For a 2-hour battery life issue, I recommend the Anker PowerCore 26800 mAh, which retails for around $79 and gets excellent reviews. It is the highest-capacity replacement battery available for most modern laptops and is compatible with virtually all major brands. You can find it on Amazon with same-day shipping. Most users report 8 to 10 hours of additional battery life after installation.`,
    flags: [
      {
        id: 'd3-a',
        span: 'The Anker PowerCore 26800 mAh, which retails for around $79',
        issue: 'Anker PowerCore is a portable charger (a power bank you plug into), NOT a replacement battery you install. The AI conflated two different products. Also the price is from a training-cutoff-era listing; current price may be different.',
        check: 'Reasoning',
      },
      {
        id: 'd3-b',
        span: 'replacement battery',
        issue: 'The user asked for a replacement battery, meaning the internal cell. The AI is recommending an external power bank. These are completely different categories of product. This is a reasoning failure at the level of "the AI did not understand the question."',
        check: 'Reasoning',
      },
      {
        id: 'd3-c',
        span: 'highest-capacity replacement battery available for most modern laptops',
        issue: 'Both wrong (it is not a replacement battery) and a fake superlative ("highest-capacity available"). No source. Date check also matters here: battery products change every few months.',
        check: 'Confidence',
      },
      {
        id: 'd3-d',
        span: '8 to 10 hours of additional battery life after installation',
        issue: 'You do not "install" a power bank. The AI is now generating fluent text that is not even internally consistent.',
        check: 'Reasoning',
      },
    ],
    verdict:
      'Fails three of the four checks. The AI did not understand the question, recommended the wrong category of product, and stated everything with full confidence. The correct response would have been: "Replacement laptop batteries depend on your exact model. Tell me your laptop model and I can help you find OEM and third-party options." Throw out, do not patch.',
  },
];

function YourTurn() {
  return (
    <Section
      id="sec-your-turn"
      label="Your turn"
      eyebrow="Three real AI outputs. Audit each one. Then compare."
      accent="amber"
    >
      <p>
        Here are three AI responses that look fine on the surface. For each
        one, run the four checks. Click on any phrase that fails an audit
        check, mark which check it failed, then reveal the model audit and
        compare to what you caught.
      </p>
      <p className="text-sm text-muted">
        Honor system. The flags are hidden until you reveal. Try to catch
        them yourself first.
      </p>
      <div className="mt-4 space-y-6">
        {DRILLS.map((d) => (
          <AuditDrill key={d.n} drill={d} />
        ))}
      </div>
    </Section>
  );
}

function AuditDrill({ drill }: { drill: Drill }) {
  const [revealed, setRevealed] = useState(false);

  // Render the AI response with the flag spans highlighted ONLY when revealed.
  const segments = useMemo(() => {
    let text = drill.aiResponse;
    const out: { text: string; flag?: Flag }[] = [];
    // Walk through flags in order of appearance in the text
    const ordered = drill.flags
      .map((f) => ({ flag: f, idx: text.indexOf(f.span) }))
      .filter((x) => x.idx >= 0)
      .sort((a, b) => a.idx - b.idx);

    let cursor = 0;
    for (const { flag, idx } of ordered) {
      if (idx > cursor) {
        out.push({ text: text.slice(cursor, idx) });
      }
      out.push({ text: flag.span, flag });
      cursor = idx + flag.span.length;
    }
    if (cursor < text.length) {
      out.push({ text: text.slice(cursor) });
    }
    return out;
  }, [drill]);

  return (
    <div className="overflow-hidden rounded-xl border border-amber/40 bg-amber/[0.03]">
      <div className="flex items-center justify-between border-b border-amber/30 bg-amber/[0.05] px-5 py-3">
        <div className="flex items-center gap-3">
          <span className="rounded-md bg-amber px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-ink">
            Drill {drill.n}
          </span>
          <span className="text-sm font-semibold text-text">{drill.title}</span>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted">
          <span>~{drill.estMinutes} min</span>
          <span className="h-3 w-px bg-amber/30" />
          <span>{drill.category}</span>
        </div>
      </div>

      <div className="space-y-4 px-5 py-5">
        <div className="rounded-md border border-line bg-ink/60 p-4">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
            The prompt
          </p>
          <p className="mt-2 text-sm text-text">{drill.promptShown}</p>
        </div>

        <div className="rounded-md border border-line bg-surface p-4">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
            The AI response
          </p>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-text">
            {revealed
              ? segments.map((seg, i) =>
                  seg.flag ? (
                    <span
                      key={i}
                      className="rounded bg-amber/25 px-0.5 underline decoration-amber decoration-2 underline-offset-2"
                    >
                      {seg.text}
                    </span>
                  ) : (
                    <span key={i}>{seg.text}</span>
                  )
                )
              : drill.aiResponse}
          </p>
        </div>

        {!revealed && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-line bg-ink/40 p-4">
            <p className="text-sm text-muted">
              Mark what you'd flag in your head. Which checks fail? Then
              reveal.
            </p>
            <button
              onClick={() => setRevealed(true)}
              className="rounded-md bg-amber px-4 py-2 text-xs font-semibold text-ink hover:bg-amber-hover"
            >
              Reveal model audit →
            </button>
          </div>
        )}

        {revealed && (
          <div className="space-y-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-amber">
              Model audit · {drill.flags.length} flags found
            </p>
            <ol className="space-y-3">
              {drill.flags.map((f) => (
                <li
                  key={f.id}
                  className="rounded-md border border-amber/30 bg-amber/[0.04] p-3"
                >
                  <p className="font-mono text-[10px] uppercase tracking-widest text-amber">
                    {f.check} check
                  </p>
                  <p className="mt-1 text-sm italic text-text">"{f.span}"</p>
                  <p className="mt-2 text-sm text-text/90">{f.issue}</p>
                </li>
              ))}
            </ol>
            <div className="rounded-md border border-line bg-ink/40 p-3">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
                Verdict
              </p>
              <p className="mt-1 text-sm text-text">{drill.verdict}</p>
            </div>
            <button
              onClick={() => setRevealed(false)}
              className="text-xs text-muted hover:text-text"
            >
              ← Hide audit and re-read
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Quick check ----------

function QuickCheck() {
  const questions = [
    {
      q: 'You ask an AI "what is the current version of iOS?" and it gives you a confident answer with no caveat. Which check fires first, and why?',
      a: 'Date check. iOS gets a major release every year and point releases in between, so "current" is by definition a moving target. Without a cited source from the present, you cannot trust the AI to know what shipped after its training cutoff. The right response would be either "I will look it up" (RAG) or "as of my training in X, the latest was Y, but check the current docs."',
    },
    {
      q: 'An AI cites three studies that support its argument. What do you do?',
      a: 'Source check: search for each study and read the abstract. Fabricated citations are one of the most common AI failure modes, and the only way to catch them is to look. If even one citation is fake, the whole argument is suspect.',
    },
    {
      q: 'Why is "throw it out" sometimes a better move than "ask the AI to fix it"?',
      a: 'Because the AI generates each response from scratch, with no real understanding of why the last one was wrong. Asking for a fix often produces a new, equally confident, equally wrong answer. If the audit found problems in three of the four checks, the model has no reliable signal to work from. Start over with a different approach, a different tool, or a person.',
    },
    {
      q: 'The AI gives you a paragraph where every claim sounds equally certain. Some you can verify; some you cannot. What does that tell you?',
      a: 'Confidence check fails. The AI does not know what it does not know, so it cannot adjust its tone. Uniform confidence across mixed-quality claims is a red flag by itself, before you have verified anything. A trustworthy answer hedges where it should.',
    },
    {
      q: 'Why is the date check first in the audit order?',
      a: 'Because if the answer depends on something after the training cutoff and the AI did not look it up, the rest of the checks are moot. You are auditing fiction. The date check is a fast filter that often kills the whole answer in 10 seconds.',
    },
  ];
  return (
    <Section
      id="sec-quick-check"
      label="Quick check"
      eyebrow="Answer in your head, then reveal."
      accent="rose"
    >
      <p className="text-sm text-muted">
        Self-graded. No record. The point is to find what did not stick.
      </p>
      <div className="mt-5 space-y-3">
        {questions.map((q, i) => (
          <QuickCheckItem key={i} n={i + 1} {...q} />
        ))}
      </div>
    </Section>
  );
}

function QuickCheckItem({ n, q, a }: { n: number; q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-line bg-surface">
      <div className="flex items-start gap-3 p-5">
        <span className="mt-0.5 font-mono text-[11px] uppercase tracking-widest text-amber">
          Q{n}
        </span>
        <div className="flex-1">
          <p className="text-text">{q}</p>
          {open && (
            <div className="mt-3 rounded-md border border-amber/30 bg-amber/[0.04] p-3 text-sm text-text">
              <p className="font-mono text-[10px] uppercase tracking-widest text-amber">
                Model answer
              </p>
              <p className="mt-1.5">{a}</p>
            </div>
          )}
        </div>
        <button
          onClick={() => setOpen((v) => !v)}
          className="rounded-md border border-line px-3 py-1.5 text-xs text-muted hover:border-amber hover:text-text"
        >
          {open ? 'Hide' : 'Reveal'}
        </button>
      </div>
    </div>
  );
}

// ---------- Lock card ----------

function LockCard() {
  return (
    <Section
      id="sec-lock-card"
      label="Lock card"
      eyebrow="Screenshot this. Take it into every AI conversation."
    >
      <div className="rounded-xl border border-amber/40 bg-gradient-to-br from-amber/[0.05] to-amber/[0.02] p-8" data-lockcard-body>
        <div className="flex items-baseline justify-between">
          <p className="font-mono text-[11px] uppercase tracking-widest text-amber">
            Lesson 1.4 · Lock card
          </p>
          <button className="text-xs text-muted hover:text-text">
            ⤓ Save image
          </button>
        </div>

        <p className="mt-4 text-lg leading-relaxed text-text">
          An audit is the structured way to ask whether an AI answer is
          actually true or just sounds true. Run these four checks, in order,
          on every AI response you plan to use.
        </p>

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <CheckTile
            n={1}
            name="Date"
            line="Could this depend on something after the model's training cutoff?"
          />
          <CheckTile
            n={2}
            name="Source"
            line="For every factual claim: can you actually find the cited source?"
          />
          <CheckTile
            n={3}
            name="Reasoning"
            line="Does each step actually follow from the step before it?"
          />
          <CheckTile
            n={4}
            name="Confidence"
            line="Is the AI hedging where it should, certain only where it has reason to be?"
          />
        </div>

        <div className="mt-6 rounded-lg border border-line bg-ink/60 p-5">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
            When the audit finds something
          </p>
          <ul className="mt-2 space-y-1.5 text-sm text-text/90">
            <li>
              <span className="font-mono text-amber">1.</span> Verify it
              yourself. Highest value.
            </li>
            <li>
              <span className="font-mono text-amber">2.</span> Ask the AI to
              fix it. Then audit the fix.
            </li>
            <li>
              <span className="font-mono text-amber">3.</span> Throw it out.
              Sometimes the right move.
            </li>
          </ul>
        </div>

        <p className="mt-6 text-sm italic text-muted">
          The point of an audit is not to be paranoid. It is to know which AI
          answers you can build on, and which you cannot.
        </p>
      </div>
    </Section>
  );
}

function CheckTile({
  n,
  name,
  line,
}: {
  n: number;
  name: string;
  line: string;
}) {
  return (
    <div className="rounded-lg border border-line bg-ink/60 p-4">
      <div className="flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-md bg-amber font-mono text-xs font-semibold text-ink">
          {n}
        </span>
        <p className="font-mono text-[11px] uppercase tracking-widest text-amber">
          {name} check
        </p>
      </div>
      <p className="mt-2 text-sm text-text/90">{line}</p>
    </div>
  );
}

// ---------- Exercise shell ----------

function ExerciseShell({
  number,
  title,
  estMinutes,
  type,
  children,
}: {
  number: number;
  title: string;
  estMinutes: number;
  type: 'guided' | 'captured';
  children: React.ReactNode;
}) {
  return (
    <div className="my-8 overflow-hidden rounded-xl border border-amber/40 bg-amber/[0.03]">
      <div className="flex items-center justify-between border-b border-amber/30 bg-amber/[0.05] px-5 py-3">
        <div className="flex items-center gap-3">
          <span className="rounded-md bg-amber px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-ink">
            Exercise {number}
          </span>
          <span className="text-sm font-semibold text-text">{title}</span>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted">
          <span>~{estMinutes} min</span>
          <span className="h-3 w-px bg-amber/30" />
          <span className="capitalize">{type}</span>
        </div>
      </div>
      <div className="space-y-3 px-5 py-4 text-sm text-text/90">{children}</div>
    </div>
  );
}

// ---------- Shared primitives ----------

function NextUp() {
  return (
    <div className="mt-12 rounded-lg border border-line bg-surface p-6" data-nextup-card>
      <p className="font-mono text-[11px] uppercase tracking-widest text-muted">
        Up next · Lesson 1.5
      </p>
      <div className="mt-2 flex items-start justify-between gap-6">
        <div>
          <h3 className="text-xl font-semibold text-text">
            Where AI Sits and What Happens to Your Data
          </h3>
          <p className="mt-2 text-sm text-muted">
            You have the audit. Now you need to know where your inputs go
            after you hit send. 1.5 walks through where the models actually
            live, what gets logged, what gets used for training, and how to
            decide what is safe to paste into which tool.
          </p>
        </div>
        <button className="shrink-0 rounded-md border border-line px-4 py-2 text-sm text-muted hover:border-amber hover:text-text">
          Coming soon
        </button>
      </div>
    </div>
  );
}

function Vocab({ children }: { children: React.ReactNode }) {
  return (
    <span className="border-b border-dotted border-amber/60 text-text">
      {children}
    </span>
  );
}

function Section({
  id,
  label,
  eyebrow,
  accent = 'plain',
  children,
}: {
  id: string;
  label: string;
  eyebrow: string;
  accent?: 'amber' | 'blue' | 'rose' | 'plain';
  children: React.ReactNode;
}) {
  const colorMap = {
    amber: 'text-amber',
    blue: 'text-[#7DC8E8]',
    rose: 'text-[#E89F8B]',
    plain: 'text-text',
  };
  return (
    <section
      id={id}
      className="scroll-mt-24 border-t border-line py-12 first-of-type:border-t-0 first-of-type:pt-0"
    >
      <div className="mb-6">
        <p
          className={`font-mono text-[11px] uppercase tracking-[0.18em] ${colorMap[accent]}`}
        >
          {label}
        </p>
        <p className="mt-1.5 text-sm text-muted">{eyebrow}</p>
      </div>
      <div className="space-y-4 text-base leading-relaxed text-text/90">
        {children}
      </div>
    </section>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    { id: 'sec-cold-open', label: 'Cold open' },
    { id: 'sec-from-last-time', label: 'From last time' },
    { id: 'sec-teaching', label: 'Teaching' },
    { id: 'sec-your-turn', label: 'Your turn' },
    { id: 'sec-quick-check', label: 'Quick check' },
    { id: 'sec-lock-card', label: 'Lock card' },
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ul className="mt-4 space-y-1">
        {items.map((it, i) => {
          const active = i === activeIndex;
          const done = i < activeIndex;
          return (
            <li key={it.id}>
              <a
                href={`#${it.id}`}
                className={`flex items-center gap-3 rounded-md px-2.5 py-2 text-sm transition-colors ${
                  active
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:bg-elevated/60 hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-full border font-mono text-[10px] ${
                    active
                      ? 'border-amber bg-amber text-ink'
                      : done
                      ? 'border-amber/40 text-amber'
                      : 'border-line text-muted'
                  }`}
                >
                  {done ? '✓' : i + 1}
                </span>
                {it.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}
